import { zodResolver } from "@hookform/resolvers/zod";
import { useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { formFields } from "./constants";
import { createAccountSchema } from "./schema";

import logo from "@/assets/logo/promptlayer-cake-color.png";
import AuthSubmitButton from "@/components/AuthSubmitButton";
import { Button } from "@/components/ui/button";
import { FormInput } from "@/components/ui/FormInput";
import { SocialButton } from "@/components/ui/SocialButton";
import { SocialProvider } from "@/components/ui/SocialButton/constants";
import { AUTH_CONFIG } from "@/constants";
import { GoogleReCaptchaProvider } from "@/providers";
import { useSignup } from "@/queries/auth";

const CreateAccountForm = () => {
  const { hasSSO, isOnPrem } = AUTH_CONFIG;
  const [showCaptchaChallenge, setShowCaptchaChallenge] = useState(false);
  const { mutate: createAccount, isPending: isCreateAccountLoading } = useSignup(verdict => {
    if (verdict === "challenge") {
      setShowCaptchaChallenge(true);
    }
  });
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const form = useForm({
    mode: "onChange",
    defaultValues: {
      email: "",
      name: "",
      password: "",
      verifyPassword: "",
    },
    resolver: zodResolver(createAccountSchema),
  });

  const handleCaptchaSuccess = (token: string | null) => {
    if (!token) {
      return;
    }

    setShowCaptchaChallenge(false);

    createAccount({
      email: form.getValues().email,
      name: form.getValues().name,
      password: form.getValues().password,
      recaptcha_response: token,
    });
  };

  const onSubmit = () => {
    createAccount({
      email: form.getValues().email,
      name: form.getValues().name,
      password: form.getValues().password,
    });
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center gap-2">
        <img src={logo} alt="logo" className="h-[52px] w-[52px]" />
        <span className="text-3xl font-bold text-gray-900">Sign Up</span>
      </div>
      <form className="flex flex-col gap-3 pt-3" onSubmit={form.handleSubmit(onSubmit)}>
        {formFields.map(field => (
          <FormInput
            {...form.register(field.name as keyof z.infer<typeof createAccountSchema>)}
            key={field.name}
            label={field.label}
            placeholder={field.placeholder}
            type={field.type}
            error={
              form.formState.errors[field.name as keyof z.infer<typeof createAccountSchema>]
                ?.message
            }
          />
        ))}
        {showCaptchaChallenge && (
          <div className="flex h-fit justify-end">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={import.meta.env.REACT_APP_RECAPTCHA_V2_SITE_KEY}
              onChange={handleCaptchaSuccess}
            />
          </div>
        )}
        <AuthSubmitButton
          loading={isCreateAccountLoading}
          className={showCaptchaChallenge ? "!mt-0" : ""}
          disabled={!form.formState.isValid}
          text="Create Account"
        />
      </form>
      {!hasSSO && !isOnPrem && (
        <div className="flex flex-col gap-2">
          <div className="my-2 flex items-center gap-2">
            <div className="h-[1px] w-full bg-gray-300" />
            <span className="px-2 text-sm font-light text-gray-800">OR</span>
            <div className="h-[1px] w-full bg-gray-300" />
          </div>
          <div className="mb-2 flex justify-center gap-2">
            <SocialButton size="icon" provider={SocialProvider.google} />
            <SocialButton size="icon" provider={SocialProvider.github} />
            <SocialButton size="icon" provider={SocialProvider.gitlab} />
          </div>
        </div>
      )}
      <div className="text-center text-sm text-gray-600">
        Already have an account?{" "}
        <Button variant="inlineLink" onClick={() => navigate("/login")}>
          Sign In
        </Button>
      </div>
      <div className="mt-2 text-center text-sm text-gray-600">
        <span className="flex justify-center gap-2 text-xs">
          <a
            href="https://dashboard.promptlayer.com/tos.html"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-800"
          >
            Terms of Use
          </a>
          |
          <a
            href="https://dashboard.promptlayer.com/privacy_policy.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-800"
          >
            Privacy Policy
          </a>
        </span>
      </div>
    </>
  );
};

const CreateAccount = () => (
  <GoogleReCaptchaProvider>
    <CreateAccountForm />
  </GoogleReCaptchaProvider>
);

export default CreateAccount;
